import isNumber from 'lodash/isNumber';

import { pConServiceProvider } from '@egr/xbox/pcon-login-api/ServicesProvider';
import { ignoreAjaxErrorHandlerMessageCallback } from '@egr/xbox/utils/AjaxErrorHandler';
import { getUUID } from '@egr/xbox/utils/Helper';
import { getEnvNumber } from '@egr/xbox/utils/ReactScriptHelper';
import { addBreadcrumb } from '@egr/xbox/utils/errors/Breadcrumb';
import { sentryShouldIgnoreError } from '@egr/xbox/utils/errors/Error';

import { Service } from '@easterngraphics/pcon-services';
import { ajax } from '@easterngraphics/wcf/modules/utils/async';
import { isNotNullOrEmpty, joinPath } from '@easterngraphics/wcf/modules/utils/string';

export const DEFAULT_OWDS_TIMEOUT = getEnvNumber('DEFAULT_OWDS_TIMEOUT', 120000);

interface SharedFile {
    url: string;
    expirationTimestamp: string;
}

interface OWDSUploadInformation {
    filename: string;
    content: ArrayBuffer | Blob;
    accessToken?: string;
    purpose?: string;
    timeout?: number;
}

interface CreateFileResponse {
    file_upload_token: string;
    file_expiration: string;
}

export const purposeExpiryMap = new Map<string, number>();

export async function getExpirationDays(purpose: string | undefined, apiOverride?: string): Promise<number | null> {
    if (purpose == null) {
        return null;
    }

    const savedValue = purposeExpiryMap.get(purpose);
    if (savedValue) {
        return savedValue;
    }

    try {
        const result = await fetch(joinPath(apiOverride ?? pConServiceProvider.getUrl(Service.OWDS), 'api/v1/shared_files/purposes', purpose, 'expiration_days'));
        if (result.status === 200) {
            const value = await result.json();
            if (isNumber(value)) {
                if (purpose === 'P-PL-Web_saved_project' && value > 90) {
                    purposeExpiryMap.set(purpose, value - 90);
                    return value - 90;
                }
                purposeExpiryMap.set(purpose, value);
                return value;
            }
        }
    } catch { /* */ }

    return null;
}

function createFile(uploadInfo: OWDSUploadInformation): Promise<CreateFileResponse> {
    const { content, accessToken, purpose, filename: file_name } = uploadInfo;

    const fileSize = content instanceof Blob ? content.size : content.byteLength;

    const headers = isNotNullOrEmpty(accessToken) ? {
        'Authorization': `Bearer ${accessToken}`
    } : undefined;

    return ajax<CreateFileResponse>(
        'POST',
        joinPath(pConServiceProvider.getUrl(Service.OWDS), 'api/v1/shared_files'),
        {
            file_name,
            file_size: fileSize,
            purpose
        },
        {
            dataType: 'json',
            responseType: 'json',
            retryAttempts: 0,
            headers
        },
        ignoreAjaxErrorHandlerMessageCallback
    );
}

async function uploadFile(uploadInfo: OWDSUploadInformation, createFileResponse: CreateFileResponse): Promise<string> {
    const { timeout, content, filename } = uploadInfo;

    const response = await ajax<{uri: string}>(
        'POST',
        joinPath(pConServiceProvider.getUrl(Service.OWDS), 'api/v2/shared_files/upload', createFileResponse.file_upload_token),
        content,
        {
            responseType: 'json',
            retryAttempts: 0,
            timeout: timeout ?? DEFAULT_OWDS_TIMEOUT
        },
        (error: Error | string, xhr: XMLHttpRequest, errorResponse: unknown): Error => {
            if (
                error instanceof Error &&
                error.message.includes('timeout')
            ) {
                sentryShouldIgnoreError(error);
                try {
                    addBreadcrumb({
                        category: 'OWDS upload timeout',
                        message: filename
                    });
                } catch { /* */ }
            }

            return ignoreAjaxErrorHandlerMessageCallback(error, xhr, errorResponse);
        }
    );
    return response.uri;
}

async function shared_files(uploadInfo: OWDSUploadInformation): Promise<SharedFile> {
    const uuid = getUUID();
    addBreadcrumb({ category: 'owds', message: JSON.stringify(['createFile', uploadInfo.filename, uploadInfo.accessToken != null, uuid]) });
    const createFileResponse = await createFile(uploadInfo);

    addBreadcrumb({ category: 'owds', message: JSON.stringify(['uploadFile', uploadInfo.filename, uploadInfo.accessToken != null, uuid]) });
    const url = await uploadFile(uploadInfo, createFileResponse);

    return {
        url,
        expirationTimestamp: createFileResponse.file_expiration
    };
}

/**
 * should be used for clientside generated content
 */
export const getOWDSShareFile = shared_files;
export const getOWDSShareFileUrl = async (uploadInfo: OWDSUploadInformation): Promise<string> => {
    const response = await shared_files(uploadInfo);
    return response.url;
};